import { memo } from 'react';
import type { LayoutCoupleSymbol } from './useFamilyTreeLayout';
import { SYMBOL_W } from './useFamilyTreeLayout';

// ─── Farben & Stile pro Beziehungstyp ────────────────────────────────────────

interface CoupleStyle {
  lineStroke: string;
  lineWidth: number;
  lineDash?: string;
}

const STYLES: Record<string, CoupleStyle> = {
  married:     { lineStroke: '#3b82f6', lineWidth: 2 },
  partnership: { lineStroke: '#8b5cf6', lineWidth: 2, lineDash: '6 3' },
  divorced:    { lineStroke: '#9ca3af', lineWidth: 1.5, lineDash: '4 4' },
  separated:   { lineStroke: '#9ca3af', lineWidth: 1.5, lineDash: '2 5' },
};
const DEFAULT_STYLE: CoupleStyle = { lineStroke: '#94a3b8', lineWidth: 1.5 };

// ─── Symbol-Renderer ─────────────────────────────────────────────────────────

/** Zwei ineinander verschlungene Ringe (⚭) für verheiratete Paare */
function RingsSymbol({ x, y, color }: { x: number; y: number; color: string }) {
  const r = 8;
  const gap = 5;
  const cx1 = x - gap / 2;
  const cx2 = x + gap / 2;

  return (
    <g>
      <circle cx={cx1} cy={y} r={r} fill="none" stroke={color} strokeWidth={2} />
      <circle cx={cx2} cy={y} r={r} fill="none" stroke={color} strokeWidth={2} />
    </g>
  );
}

/** Herz (♥) für Partnerschaften */
function HeartSymbol({ x, y, color }: { x: number; y: number; color: string }) {
  // Herz-Pfad zentriert auf (x, y)
  const s = 9;
  return (
    <path
      d={`
        M${x},${y + s * 0.35}
        C${x},${y - s * 0.1} ${x - s},${y - s * 0.5} ${x - s},${y - s * 0.1}
        C${x - s},${y + s * 0.35} ${x},${y + s * 0.9} ${x},${y + s * 0.9}
        C${x},${y + s * 0.9} ${x + s},${y + s * 0.35} ${x + s},${y - s * 0.1}
        C${x + s},${y - s * 0.5} ${x},${y - s * 0.1} ${x},${y + s * 0.35}
        Z
      `}
      fill={color}
      opacity={0.85}
    />
  );
}

/** Durchgestrichene Ringe für geschiedene/getrennte Paare */
function BrokenRingsSymbol({ x, y, color }: { x: number; y: number; color: string }) {
  const r = 8;
  const gap = 5;
  const cx1 = x - gap / 2;
  const cx2 = x + gap / 2;

  return (
    <g>
      <circle cx={cx1} cy={y} r={r} fill="none" stroke={color} strokeWidth={1.5} strokeDasharray="3 2" />
      <circle cx={cx2} cy={y} r={r} fill="none" stroke={color} strokeWidth={1.5} strokeDasharray="3 2" />
      {/* Diagonale Linie */}
      <line
        x1={x - r - 2} y1={y + r + 2}
        x2={x + r + 2} y2={y - r - 2}
        stroke={color} strokeWidth={1.5}
      />
    </g>
  );
}

/** Neutrales Symbol (kleine Raute) für informelle Elternschaft ohne Beziehungseintrag */
function NeutralSymbol({ x, y }: { x: number; y: number }) {
  return (
    <circle cx={x} cy={y} r={5} fill="#cbd5e1" />
  );
}

// ─── PartnershipLayer ────────────────────────────────────────────────────────

interface PartnershipLayerProps {
  symbols: LayoutCoupleSymbol[];
}

export const PartnershipLayer = memo(function PartnershipLayer({ symbols }: PartnershipLayerProps) {
  return (
    <g className="partnership-layer">
      {symbols.map((sym, i) => {
        const style = sym.link ? (STYLES[sym.link.type] ?? DEFAULT_STYLE) : DEFAULT_STYLE;
        const color = style.lineStroke;

        // Horizontale Linie: von rechter Kante Person1 bis linke Kante Person2
        // (durch das Symbol hindurch)
        const lineLeft  = sym.x - SYMBOL_W / 2 - 2;
        const lineRight = sym.x + SYMBOL_W / 2 + 2;
        const p1RightEdge = sym.x - SYMBOL_W / 2 - 20;
        const p2LeftEdge  = sym.x + SYMBOL_W / 2 + 20;

        const type = sym.link?.type ?? 'none';

        return (
          <g key={i}>
            {/* Verbindungslinie links: Person1 → Symbol */}
            <line
              x1={p1RightEdge} y1={sym.y}
              x2={lineLeft}    y2={sym.y}
              stroke={color}
              strokeWidth={style.lineWidth}
              strokeDasharray={style.lineDash}
              opacity={0.85}
            />
            {/* Verbindungslinie rechts: Symbol → Person2 */}
            <line
              x1={lineRight}  y1={sym.y}
              x2={p2LeftEdge} y2={sym.y}
              stroke={color}
              strokeWidth={style.lineWidth}
              strokeDasharray={style.lineDash}
              opacity={0.85}
            />

            {/* Symbol */}
            {type === 'married' && (
              <RingsSymbol x={sym.x} y={sym.y} color={color} />
            )}
            {type === 'partnership' && (
              <HeartSymbol x={sym.x} y={sym.y} color={color} />
            )}
            {(type === 'divorced' || type === 'separated') && (
              <BrokenRingsSymbol x={sym.x} y={sym.y} color={color} />
            )}
            {type === 'none' && (
              <NeutralSymbol x={sym.x} y={sym.y} />
            )}
          </g>
        );
      })}
    </g>
  );
});
